import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { Section } from './ui/Section';
import { FadeIn } from './ui/FadeIn';
function AnimatedVaultLock() {
  return <div className="flex flex-col items-center justify-center relative" data-id="element-228">
      {/* Outer rotating ring */}
      <div className="relative w-48 h-48 md:w-56 md:h-56" data-id="element-229">
        {/* Ambient glow */}
        <motion.div className="absolute inset-0 rounded-full" animate={{
        boxShadow: ['0 0 30px rgba(184, 155, 94, 0.1), 0 0 60px rgba(184, 155, 94, 0.05)', '0 0 50px rgba(184, 155, 94, 0.25), 0 0 100px rgba(184, 155, 94, 0.1)', '0 0 30px rgba(184, 155, 94, 0.1), 0 0 60px rgba(184, 155, 94, 0.05)']
      }} transition={{
        repeat: Infinity,
        duration: 4,
        ease: 'easeInOut'
      }} data-id="element-230" />

        {/* Outer decorative ring — slow counter-rotate */}
        <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" animate={{
        rotate: -360
      }} transition={{
        repeat: Infinity,
        duration: 30,
        ease: 'linear'
      }} data-id="element-231">
          <defs data-id="element-232">
            <linearGradient id="ringGrad1" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-233">
              <stop offset="0%" stopColor="#B89B5E" stopOpacity="0.8" data-id="element-234" />
              <stop offset="50%" stopColor="#D4BF8A" stopOpacity="0.3" data-id="element-235" />
              <stop offset="100%" stopColor="#B89B5E" stopOpacity="0.8" data-id="element-236" />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="96" stroke="url(#ringGrad1)" strokeWidth="1" fill="none" data-id="element-237" />
          {/* Tick marks */}
          {Array.from({
          length: 36
        }).map((_, i) => {
          const angle = i * 10 * Math.PI / 180;
          const isMajor = i % 3 === 0;
          const r1 = isMajor ? 88 : 91;
          const r2 = 96;
          return <line key={i} x1={100 + r1 * Math.sin(angle)} y1={100 - r1 * Math.cos(angle)} x2={100 + r2 * Math.sin(angle)} y2={100 - r2 * Math.cos(angle)} stroke="#B89B5E" strokeWidth={isMajor ? 1.5 : 0.5} opacity={isMajor ? 0.6 : 0.25} data-id="element-238" />;
        })}
        </motion.svg>

        {/* Middle ring — slow rotate */}
        <motion.svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" animate={{
        rotate: 360
      }} transition={{
        repeat: Infinity,
        duration: 45,
        ease: 'linear'
      }} data-id="element-239">
          <defs data-id="element-240">
            <linearGradient id="ringGrad2" x1="0%" y1="0%" x2="100%" y2="0%" data-id="element-241">
              <stop offset="0%" stopColor="#D4BF8A" stopOpacity="0.6" data-id="element-242" />
              <stop offset="25%" stopColor="#B89B5E" stopOpacity="0.15" data-id="element-243" />
              <stop offset="50%" stopColor="#D4BF8A" stopOpacity="0.6" data-id="element-244" />
              <stop offset="75%" stopColor="#B89B5E" stopOpacity="0.15" data-id="element-245" />
              <stop offset="100%" stopColor="#D4BF8A" stopOpacity="0.6" data-id="element-246" />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r="82" stroke="url(#ringGrad2)" strokeWidth="0.75" fill="none" strokeDasharray="4 8" data-id="element-247" />
        </motion.svg>

        {/* Inner static ring with gradient */}
        <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" data-id="element-248">
          <defs data-id="element-249">
            <radialGradient id="innerGlow" cx="50%" cy="50%" r="50%" data-id="element-250">
              <stop offset="0%" stopColor="#B89B5E" stopOpacity="0.08" data-id="element-251" />
              <stop offset="100%" stopColor="#B89B5E" stopOpacity="0" data-id="element-252" />
            </radialGradient>
            <linearGradient id="lockBodyGrad" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-253">
              <stop offset="0%" stopColor="#D4BF8A" data-id="element-254" />
              <stop offset="50%" stopColor="#B89B5E" data-id="element-255" />
              <stop offset="100%" stopColor="#887038" data-id="element-256" />
            </linearGradient>
            <linearGradient id="shackleGrad" x1="0%" y1="100%" x2="0%" y2="0%" data-id="element-257">
              <stop offset="0%" stopColor="#B89B5E" data-id="element-258" />
              <stop offset="100%" stopColor="#D4BF8A" data-id="element-259" />
            </linearGradient>
          </defs>
          {/* Inner glow fill */}
          <circle cx="100" cy="100" r="70" fill="url(#innerGlow)" data-id="element-260" />
          {/* Inner circle border */}
          <circle cx="100" cy="100" r="70" stroke="#B89B5E" strokeWidth="1.5" fill="none" opacity="0.4" data-id="element-261" />
        </svg>

        {/* The lock itself — animated */}
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-262">
          <motion.svg viewBox="0 0 60 72" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-16 h-20 md:w-20 md:h-24" animate={{
          scale: [1, 1.03, 1]
        }} transition={{
          repeat: Infinity,
          duration: 4,
          ease: 'easeInOut'
        }} data-id="element-263">
            {/* Lock shackle — breathing animation */}
            <motion.path d="M14 28V18C14 10.268 20.268 4 28 4H32C39.732 4 46 10.268 46 18V28" stroke="url(#shackleGrad)" strokeWidth="3" strokeLinecap="round" fill="none" animate={{
            y: [0, -3, 0],
            opacity: [0.8, 1, 0.8]
          }} transition={{
            repeat: Infinity,
            duration: 3,
            ease: 'easeInOut'
          }} data-id="element-264" />
            {/* Lock body */}
            <rect x="6" y="28" width="48" height="38" rx="4" fill="url(#lockBodyGrad)" opacity="0.15" stroke="url(#lockBodyGrad)" strokeWidth="2" data-id="element-265" />
            {/* Keyhole outer */}
            <motion.circle cx="30" cy="44" r="7" fill="none" stroke="#D4BF8A" strokeWidth="1.5" animate={{
            opacity: [0.4, 0.8, 0.4],
            r: [7, 7.5, 7]
          }} transition={{
            repeat: Infinity,
            duration: 3,
            ease: 'easeInOut'
          }} data-id="element-266" />
            {/* Keyhole inner */}
            <motion.circle cx="30" cy="44" r="3" fill="#B89B5E" animate={{
            opacity: [0.6, 1, 0.6],
            fill: ['#B89B5E', '#D4BF8A', '#B89B5E']
          }} transition={{
            repeat: Infinity,
            duration: 3,
            ease: 'easeInOut'
          }} data-id="element-267" />
            {/* Keyhole slot */}
            <motion.rect x="28.5" y="48" width="3" height="8" rx="1.5" fill="#B89B5E" animate={{
            opacity: [0.5, 0.9, 0.5]
          }} transition={{
            repeat: Infinity,
            duration: 3,
            ease: 'easeInOut'
          }} data-id="element-268" />
          </motion.svg>
        </div>

        {/* Floating particles */}
        {[0, 1, 2, 3, 4, 5].map(i => <motion.div key={i} className="absolute w-1 h-1 rounded-full bg-accent-400" style={{
        top: '50%',
        left: '50%'
      }} animate={{
        x: [0, Math.cos(i * 60 * Math.PI / 180) * 90],
        y: [0, Math.sin(i * 60 * Math.PI / 180) * 90],
        opacity: [0, 0.6, 0],
        scale: [0, 1.5, 0]
      }} transition={{
        repeat: Infinity,
        duration: 4,
        delay: i * 0.7,
        ease: 'easeOut'
      }} data-id="element-269" />)}
      </div>

      {/* Text below */}
      <motion.div className="mt-10 text-center" initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5,
      duration: 0.8
    }} data-id="element-270">
        <h3 className="font-serif text-xl md:text-2xl tracking-[0.2em] text-sage-100 mb-2" data-id="element-271">
          PRIVATE ACCESS
        </h3>
        <div className="w-12 h-[1px] bg-gradient-to-r from-transparent via-accent-400 to-transparent mx-auto mb-3" data-id="element-272" />
        <p className="text-xs text-sage-400 tracking-[0.3em] uppercase" data-id="element-273">
          Members Only
        </p>
      </motion.div>
    </div>;
}
export function HiddenMarket() {
  const tags = [{
    left: 'Board Positions',
    right: 'C-Suite Opportunities'
  }, {
    left: 'Advisory Roles',
    right: 'Strategic Partnerships'
  }];
  return <Section background="navy" id="hidden" className="relative overflow-hidden" data-id="element-274">
      {/* Gold accent line at top */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-accent-400/60 to-transparent" data-id="element-275" />

      {/* Background gradient orbs */}
      <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-[500px] h-[500px] bg-gradient-radial from-accent-400/[0.04] to-transparent rounded-full blur-3xl pointer-events-none" data-id="element-276" />
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[400px] h-[400px] bg-gradient-radial from-ink-700/30 to-transparent rounded-full blur-3xl pointer-events-none" data-id="element-277" />

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-0 items-center" data-id="element-278">
        {/* Left content */}
        <div className="lg:col-span-3 lg:pr-16" data-id="element-279">
          <FadeIn data-id="element-280">
            <h2 className="text-3xl md:text-4xl font-serif text-sage-50 mb-8 italic" data-id="element-281">
              The Hidden Market
            </h2>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-282">
            <p className="text-sage-300 text-lg leading-relaxed mb-6" data-id="element-283">
              An estimated 70% of executive positions are never publicly
              advertised. They exist in the confidential conversations of
              boardrooms and private networks.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-284">
            <p className="text-sage-300 text-lg leading-relaxed mb-10" data-id="element-285">
              Boardline opens doors that most candidates never know exist. We
              bypass the gatekeepers and connect you directly with the
              decision-makers shaping the future of your industry.
            </p>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-286">
            <div className="grid grid-cols-2 gap-x-8 gap-y-4" data-id="element-287">
              {tags.map((row, i) => <Fragment key={i} data-id="element-288">
                  <div className="flex items-center gap-3" data-id="element-289">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0" data-id="element-290" />
                    <span className="text-sage-200 font-serif text-base" data-id="element-291">
                      {row.left}
                    </span>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-292">
                    <div className="w-1.5 h-1.5 rounded-full bg-accent-400 flex-shrink-0" data-id="element-293" />
                    <span className="text-sage-200 font-serif text-base" data-id="element-294">
                      {row.right}
                    </span>
                  </div>
                </Fragment>)}
            </div>
          </FadeIn>
        </div>

        {/* Right panel — vault door */}
        <FadeIn delay={0.3} className="lg:col-span-2" data-id="element-295">
          <div className="relative bg-gradient-to-b from-ink-800/80 to-ink-900/90 border border-white/[0.06] p-10 md:p-14 flex items-center justify-center min-h-[380px] backdrop-blur-sm" data-id="element-296">
            {/* Door frame effect */}
            <div className="absolute inset-2 border border-accent-400/10" data-id="element-297" />
            {/* Corner accents — larger, more ornate */}
            <div className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-accent-400/40" data-id="element-298" />
            <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-accent-400/40" data-id="element-299" />
            <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-accent-400/40" data-id="element-300" />
            <div className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-accent-400/40" data-id="element-301" />

            <AnimatedVaultLock data-id="element-302" />
          </div>
        </FadeIn>
      </div>
    </Section>;
}